import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CapCSVFileUploader from "../CapCSVFileUploader";
import CapSpin from "../CapSpin";
import CapLabel from "../CapLabel";
import CapDropdown from "../CapDropdown";
import CapMenu from "../CapMenu";
import CapTooltip from "../CapTooltip";
import CapNotification from "../CapNotification";
import { handleStoreCodeUpload } from "./helper";
import { getCombinedNanoId } from "../CapDnDGraph/utils";
import {
  UPLOAD_LIMIT_EXCEEDED,
  UPLOAD_FAIL,
  UNSUPPORTED_CSV_HEADER,
  EMPTY_CSV_FILE,
  INPROGRESS,
  SUCCESS,
  FAILURE,
  UPLOAD,
} from "./constants";
import CapIcon from "../CapIcon";
import CapRow from "../CapRow";
import {
  FONT_COLOR_05,
  CAP_COLOR_06,
  CAP_COLOR_03,
  CAP_RED,
} from "../styled/variables";

export const ConditionStoreUploader = props => {
  const {
    handleOnSelect,
    getEntityDetails,
    uploadFailedError,
    uploadLimitExceeded,
    uploadReqLoader,
    or,
    isStoreFilter = false,
    uploadInProgressMsg,
    uploadStoreDetails,
    unsupportedCsvHeaderMsg,
    csvFileNoEntryTxt,
    downloadedMsg,
    onStoreUpload,
    storeUploadInfo,
    referenceId,
    failedStorePathIdentifier,
    blockId,
    disabledStoreUpload,
    conditionData,
    storeSource,
    uploadedStoreIds,
    numOfStoreIds,
    storeIdsMsg,
    reuploadFileMsg,
    downloadStoreIdMsg,
    downloadStoreIdCodeNameMsg,
    downloadInProgressMsg,
  } = props;

  // This is required to get the details of the uploaded stores to map correctly with the corresponding store blocks.
  const storeInfo =
    isStoreFilter &&
    storeUploadInfo?.find(storeData => (
        storeData?.referenceId === referenceId && !storeData?.isStoreRemoved
      ));
  const {
    fileName: storeFileName = "",
    uploadStatus = "",
    nsmBrokerDataFetchedStatus = "",
    uploadJobSubmittedStatus = "",
    isStoreRemoved = false,
    valid = 0,
    invalid = 0,
    referenceId: storeReferenceId = "",
    validStoreIds = uploadedStoreIds || [],
  } = storeInfo || {};
  const storeUploadIsInProgress = isStoreFilter && ![SUCCESS, FAILURE].includes(nsmBrokerDataFetchedStatus) && ![uploadJobSubmittedStatus, uploadStatus]?.includes(FAILURE);
  // This condition is required to hide the store ids with download options when reuplod file operation is performed
  const showNumberOfUploadedStoreIds = isStoreFilter && storeSource === UPLOAD  && !(
    [nsmBrokerDataFetchedStatus, uploadJobSubmittedStatus, uploadStatus]?.includes(SUCCESS) || 
    [nsmBrokerDataFetchedStatus, uploadJobSubmittedStatus, uploadStatus]?.includes(FAILURE));

  const [uploadError, setUploadError] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [uploadedStores, setUploadedStores] = useState([]);

  useEffect(() => {
    if (uploadedStores.length) {
      const uniqueStores = [...new Set([...uploadedStores])];
      handleOnSelect(uniqueStores);
    }
  }, [uploadedStores]);

  //handling different kind of errors while upload
  const uploadErrorType = type => {
    switch (type) {
      case UPLOAD_FAIL:
        setUploadError(uploadFailedError);
        break;
      case UPLOAD_LIMIT_EXCEEDED:
        setUploadError(uploadLimitExceeded);
        break;
      case UNSUPPORTED_CSV_HEADER:
        setUploadError(unsupportedCsvHeaderMsg);
        break;
      case EMPTY_CSV_FILE:
        setUploadError(csvFileNoEntryTxt);
        break;
    }
  };

  //handler for CapCSVFileUploader onChipClose callback
  const removeUploadedStores = () => {
    const updatedStores = [];
    handleOnSelect(updatedStores);
    if (isStoreFilter) {
      setUploadError(""); // Removing any error message when uploaded csv file is removed.
      const newStoreUploadInfo = storeUploadInfo?.map(data => {
        if (data?.referenceId === referenceId) {
          data.isStoreRemoved = true;
        }
        return data;
      });
      onStoreUpload(newStoreUploadInfo, {}, false);
    }
  };

  const downloadHandler = (event, data, downloadedFileName) => {
    event.target.setAttribute(
      "href",
      `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`
    );
    event.target.setAttribute("download", downloadedFileName);
  };

  // This function is to download the store ids on click of download store ids option
  const downloadStoreId = event => {
    const csvContent = validStoreIds?.join(","); // join the array with a comma delimiter
    const fileName = "storeId.csv";
    downloadHandler(event, csvContent, fileName);
    CapNotification.success({
      message: (
        <>
          <CapLabel.CapLabelInline type="label16" className="margin-r-4">
            {fileName}
          </CapLabel.CapLabelInline>
          <CapLabel.CapLabelInline type="label15">
            {downloadedMsg}
          </CapLabel.CapLabelInline>
        </>
      ),
    });
  };

  // This function is to download store name, id and code for journey context by creating a file from the available store ids to upload in file service
  const downloadStoreIdWithCodeAndName = () => {
    const csv = validStoreIds?.join("\n"); // join the array with a comma delimiter
    // create a blob object from the csv string
    const file = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const storeFilterReqObj = { file };
    const fileName = "storeIdCodeName.csv";
    const identifierType = "id";
    const storeUploadData = [
      {
        fileName,
        uploadStatus: INPROGRESS,
        referenceId: getCombinedNanoId(7, 3),
        downloadStatus: INPROGRESS,
        parentReferenceId: referenceId,
        blockId,
        s3Path: "",
        identifierType,
      },
    ];
    onStoreUpload(storeUploadData, storeFilterReqObj, true);
  };

  // This function is to provide the download store ids and store name code id menu options
  const getDownloadStoreOptions = () => {
    const { downloadStatus = "", nsmBrokerDataFetchedStatus = "", uploadStatus = "", uploadJobSubmittedStatus = "" } =
      storeUploadInfo?.filter(
        data =>
          data?.identifierType === "id" &&
          data?.parentReferenceId === referenceId
      )?.[0] || {};

    // We need to keep track of failure in 3 API call in the download process from uploading the IDs file to fileservice -> Submitting the job to BE -> Fetching data from nsmbroker.
    const isDownloadInProgress =
      downloadStatus === INPROGRESS && ![uploadStatus, uploadJobSubmittedStatus, nsmBrokerDataFetchedStatus].includes(FAILURE);
    return (
      <CapDropdown
        overlay={
          <CapMenu>
            <CapMenu.Item key="downloadStoreId" className="pointer-cursor">
              <a href onClick={downloadStoreId} type="download">
                {downloadStoreIdMsg}
              </a>
            </CapMenu.Item>
            <CapMenu.Item
              key="downloadStoreIdCodeAndName"
              className="pointer-cursor"
              disabled={isDownloadInProgress}
            >
              <CapTooltip title={isDownloadInProgress && downloadInProgressMsg}>
                <a
                  href
                  style={{ opacity: isDownloadInProgress && 0.5 }}
                  onClick={
                    !isDownloadInProgress && downloadStoreIdWithCodeAndName
                  }
                  type="download"
                >
                  {downloadStoreIdCodeNameMsg}
                </a>
              </CapTooltip>
            </CapMenu.Item>
          </CapMenu>
        }
      >
        <CapIcon
          type="download"
          size="s"
          className="pointer-cursor"
          style={{ color: FONT_COLOR_05 }}
        />
      </CapDropdown>
    );
  };

  return (
    <>
      <CapLabel>{or}</CapLabel>
      <CapRow type="flex" align="middle">
        {showNumberOfUploadedStoreIds && numOfStoreIds > 0 && (
          <CapRow type="flex" align="middle">
            {" "}
            <CapLabel type="label4" className="margin-r-10">
              {numOfStoreIds} {storeIdsMsg}{" "}
            </CapLabel>{" "}
            {getDownloadStoreOptions()}
          </CapRow>
        )}
        <CapSpin spinning={!isStoreFilter && showLoader} tip={uploadReqLoader}>
          <CapCSVFileUploader
            sampleCsvFilePath={require(isStoreFilter
              ? "./storeCodeWithoutHeader.csv"
              : "./storeCode.csv")}
            onUpload={event =>
              handleStoreCodeUpload(event, {
                setUploadedStores,
                setShowLoader,
                uploadErrorType,
                getEntityDetails,
                isStoreFilter,
                storeUploadInfo,
                onStoreUpload,
                referenceId,
                blockId,
                conditionData,
              })
            }
            errorMessage={uploadError}
            onChipClose={removeUploadedStores}
            isStoreFilter={isStoreFilter}
            storeUploadInfo={storeUploadInfo}
            referenceId={referenceId}
            failedStorePathIdentifier={failedStorePathIdentifier}
            fileName={storeFileName}
            disabled={disabledStoreUpload}
            storeSource={storeSource}
            reuploadFileMsg={reuploadFileMsg}
          />
        </CapSpin>
      </CapRow>
      {isStoreFilter && (
        <CapRow type="flex" align="middle">
          {/* This block is required to show upload in progress status with icon */}
          {storeFileName && !isStoreRemoved && storeUploadIsInProgress && (
              <>
                <CapIcon
                  type="in-progress"
                  size="s"
                  svgProps={{ fill: FONT_COLOR_05 }}
                  className="progress-icon"
                />
                <CapLabel type="label11" className="margin-l-4 margin-r-4">
                  {uploadInProgressMsg}
                </CapLabel>
              </>
            )}
          {/* This block is required to show upload failed errors */}
          {[
            uploadStatus,
            nsmBrokerDataFetchedStatus,
            uploadJobSubmittedStatus,
          ]?.includes(FAILURE) &&
            !isStoreRemoved && (
              <>
                <CapIcon
                  type="error"
                  size="s"
                  style={{ color: CAP_RED }}
                  className="margin-r-4"
                />
                <CapLabel.CapLabelInline
                  style={{ color: CAP_RED }}
                  type="label4"
                >
                  {uploadFailedError}
                </CapLabel.CapLabelInline>
              </>
            )}
          {/* This block is required to show valid/invalid stores info after successful upload with download options */}
          {!showLoader &&
            !uploadError &&
            nsmBrokerDataFetchedStatus === SUCCESS &&
            referenceId === storeReferenceId && (
              <>
                <CapLabel type="label4" className="margin-r-4">
                  {valid} valid
                </CapLabel>
                <CapIcon
                  type="check-circle"
                  size="s"
                  style={{ color: CAP_COLOR_06 }}
                />
                <CapLabel type="label4" className="margin-l-10 margin-r-4">
                  {invalid} invalid
                </CapLabel>
                <CapIcon
                  type="warning"
                  className="margin-r-10"
                  size="s"
                  style={{ color: CAP_COLOR_03 }}
                />
                {getDownloadStoreOptions()}
              </>
            )}
        </CapRow>
      )}
    </>
  );
};

ConditionStoreUploader.propTypes = {
  handleOnSelect: PropTypes.func.isRequired,
  getEntityDetails: PropTypes.func.isRequired,
  uploadFailedError: PropTypes.string.isRequired,
  uploadLimitExceeded: PropTypes.string.isRequired,
  uploadReqLoader: PropTypes.string.isRequired,
  or: PropTypes.string.isRequired,
  isStoreFilter: PropTypes.bool,
  uploadInProgressMsg: PropTypes.string,
  uploadStoreDetails: PropTypes.array,
  unsupportedCsvHeaderMsg: PropTypes.string,
  csvFileNoEntryTxt: PropTypes.string,
  disabledStoreUpload: PropTypes.bool,
  storeIdsMsg: PropTypes.string,
  storeUploadInfo: PropTypes.array,
  onStoreUpload: PropTypes.func,
  referenceId: PropTypes.string,
  storeSource: PropTypes.string,
  uploadedStoreIds: PropTypes.array,
  failedStorePathIdentifier: PropTypes.string,
  blockId: PropTypes.string,
  conditionData: PropTypes.object,
  reuploadFileMsg: PropTypes.string,
  downloadStoreIdMsg: PropTypes.string,
  downloadStoreIdCodeNameMsg: PropTypes.string,
  downloadInProgressMsg: PropTypes.string,
};
export default ConditionStoreUploader;
